import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_IMG } from "@/lib/brand";

const DEMO_ACCOUNTS = {
  rep: { email: "demorep@test.com", password: "RepPass123!" },
};

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    const demo = DEMO_ACCOUNTS[params.get("demo")];
    if (!demo) return;
    (async () => {
      setLoading(true);
      try {
        const user = await login(demo.email, demo.password);
        toast.success(`Welcome, ${user.name}`);
        nav(user.role === "sales_rep" ? "/sales/dashboard" : "/partner/dashboard", { replace: true });
      } catch {
        toast.error("Demo login unavailable");
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      toast.success(`Welcome, ${user.name}`);
      nav(user.role === "sales_rep" ? "/sales/dashboard" : "/partner/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="relative w-full max-w-md bg-white border border-slate-200 p-10 shadow-2xl" data-testid="auth-card">
        <img src={LOGO_IMG} alt="PMI" className="h-12 w-auto mb-4" />
        <Link to="/" className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">PMI Portal</Link>
        <h1 className="font-display text-3xl tracking-tight mt-3 text-navy-900">
          {mode === "login" ? "Sign in" : "Create account"}
        </h1>
        <p className="text-sm text-slate-500 mt-2 mb-8">
          {mode === "login" ? "Merchant & Sponsor Login" : "Register as a merchant partner."}
        </p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Business / your name" data-testid="auth-name"
              className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email" data-testid="auth-email"
            className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Password" data-testid="auth-password"
            className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none" />
          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="w-full bg-navy-900 text-white py-4 font-[600] hover:bg-emerald-700 transition-colors duration-200 disabled:opacity-60">
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="auth-toggle-mode"
          className="mt-6 text-sm text-slate-500 hover:text-navy-900 transition-colors">
          {mode === "login" ? "Need a partner account? Register" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
